import { useState, useCallback, cloneElement, Children, useEffect } from 'react'
import PropTypes from 'prop-types'

function AccordionHeader({
  className,
  indexChild,
  activeSection,
  toggle,
  children,
}) {
  return (
    <div onClick={() => toggle(indexChild)} className={className}>
      {typeof children === 'function' ? children(activeSection) : children}
    </div>
  )
}

AccordionHeader.propTypes = {
  className: PropTypes.string,
  indexChild: PropTypes.number,
  activeSection: PropTypes.arrayOf(PropTypes.number),
  toggle: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func,
  ]),
}

function AccordionContent({ children }) {
  return <>{children}</>
}

AccordionContent.propTypes = {
  children: PropTypes.node,
}

function AccordionSection({
  className,
  indexChild,
  activeSection,
  toggle,
  children,
}) {
  const clonedChildren = Children.map(children, child => {
    const component = {}

    if (!child) return null

    if (child.type.toString() === AccordionHeader.toString()) {
      component.header = cloneElement(child, {
        indexChild,
        toggle,
        activeSection,
      })
    }

    if (
      child.type.toString() === AccordionContent.toString() &&
      activeSection.includes(indexChild)
    ) {
      component.content = child
    }

    return (
      <>
        {component.header}
        {component.content}
      </>
    )
  })

  return <div className={className}>{clonedChildren}</div>
}

AccordionSection.propTypes = {
  className: PropTypes.string,
  indexChild: PropTypes.number,
  activeSection: PropTypes.arrayOf(PropTypes.number),
  toggle: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
}

function Accordion({ active, className, multiple, onToggle, children, overrideActive }) {
  const [activeSection, setActiveSection] = useState(active)

  useEffect(() => {
    if (Array.isArray(overrideActive)) {
      setActiveSection(overrideActive)
    }   
  }, [overrideActive])

  const toggle = useCallback(
    index => {
      const currentActiveSection = [...activeSection]
      const isCurrentActiveSectionHasIndex =
        currentActiveSection.includes(index)
      const indexOfCurrentSection = currentActiveSection.indexOf(index)

      if (onToggle) onToggle(index)

      if (multiple && isCurrentActiveSectionHasIndex) {
        if (indexOfCurrentSection !== -1) {
          currentActiveSection.splice(indexOfCurrentSection, 1)
        }
        return setActiveSection(currentActiveSection)
      }

      if (multiple && !isCurrentActiveSectionHasIndex) {
        return setActiveSection(currentActiveSection.concat(index))
      }

      if (!multiple && !isCurrentActiveSectionHasIndex) {
        return setActiveSection([index])
      }

      return setActiveSection([])
    },
    [activeSection, multiple, onToggle]
  )

  const clonedChildren = Children.map(children, (child, index) => {
    if (child) {
      return cloneElement(child, {
        activeSection,
        indexChild: index,
        toggle,
      })
    }

    return null
  })

  return <div className={className}>{clonedChildren}</div>
}

Accordion.propTypes = {
  active: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
  multiple: PropTypes.bool,
  onToggle: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  overrideActive: PropTypes.arrayOf(PropTypes.number),
}

Accordion.defaultProps = {
  active: [],
  multiple: true,
}

Accordion.Section = AccordionSection
Accordion.Header = AccordionHeader
Accordion.Content = AccordionContent

export default Accordion
